import { useLocation } from "react-router-dom";
import React, { useContext, useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import moment from "moment";
import * as Yup from "yup";
// components
import Spinner from "../Spinner";
// firebase
import { FirebaseContext } from "../../firebaseComponents";
// util
import { showErrors, updateDateFormat } from "../../utils/helpers";
// data
import { vialMeta } from "../../data/vialMeta";
// styles
import styles from "./forms.module.css";

const requiredString = "required";

export default function FormRequest({
  dateField,
  formName,
  formNameReadable,
  formPageName, // use to determine which vial meta we will select
  submissionMessage,
}) {
  const firebase = useContext(FirebaseContext);
  let location = useLocation();
  let thanks = location.search.includes("thanks");
  const [fileError, setFileError] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showSubmissionMessage, setShowSubmissionMessage] = useState(thanks);
  const today = moment().format("YYYY-MM-DD");
  const [maxPurchaseDate, setMaxPurchaseDate] = useState(today);
  // item meta data requested by the client
  const [pageVialMeta, setPageVialMeta] = useState();
  const [item, setItem] = useState("");
  const [internalName, setInternalName] = useState("");
  const [itemDescription, setItemDescription] = useState("");

  const initialValues = {
    requestedVials: "",
    vialStrength: "",
    item: "",
    internalName: "",
    itemDescription: "",
    invoiceNumber: "",
    purchaseDate: "",
    contact: {
      hospitalName: "",
      authorizedRepresentativeName: "",
      authorizedRepresentativeTitle: "",
      email: "",
      phoneNumber: "",
    },
    shipping: {
      address1: "",
      address2: "",
      city: "",
      state: "",
      zipCode: "",
    },
    purchaseOrderNumber: "",
    authorizationVerification: false,
    termsVerify: false,
    eSignature: "",
    date: "",
    upload: "",
  };

  const getExtension = (file) => {
    const lastDot = file.name.lastIndexOf(".");
    if (lastDot === -1) return "";
    return file.name.substring(lastDot + 1);
  };

  // more info about validation can be found below
  // - https://jaredpalmer.com/formik/docs/guides/validation#form-level-validation
  // - https://github.com/jquense/yup#number
  let Schema = Yup.object().shape({
    requestedVials: Yup.number()
      .min(0, "Please enter a number")
      .required(requiredString),
    vialStrength: Yup.string()
      .required(requiredString)
      .oneOf([
        "GIAPREZA 0.5 mg vial NDC: 68547-005-01",
        "GIAPREZA 2.5 mg vial NDC: 68547-501-02",
      ]),
    purchaseDate: Yup.string().required(requiredString).default(""),
    contact: Yup.object().shape({
      hospitalName: Yup.string().required(requiredString),
      authorizedRepresentativeName: Yup.string().required(requiredString),
      authorizedRepresentativeTitle: Yup.string().required(requiredString),
      email: Yup.string().required(requiredString),
      phoneNumber: Yup.string().required(requiredString),
    }),
    shipping: Yup.object().shape({
      address1: Yup.string().required(requiredString),
      address2: Yup.string(),
      city: Yup.string().required(requiredString),
      state: Yup.string().required(requiredString),
      zipCode: Yup.string().required(requiredString),
    }),
    purchaseOrderNumber: Yup.string().required(requiredString),
    authorizationVerification: Yup.boolean()
      .oneOf([true], requiredString)
      .required(requiredString),
    termsVerify: Yup.boolean()
      .oneOf([true], requiredString)
      .required(requiredString),
    eSignature: Yup.string().required(requiredString),
    date: Yup.string().required(requiredString),
    upload: Yup.mixed().required(
      "Please submit a copy of your hospital state license."
    ),
  });

  if (dateField) {
    initialValues[dateField.name] = "";
    Schema = Schema.shape({
      [dateField.name]: Yup.string().required(requiredString),
    });
  }

  const updateDateField = (val, values, setFieldValue) => {
    //purchase date must be before adminstration/mixed date
    setFieldValue(dateField.name, val, false);
    if (val) setMaxPurchaseDate(val);
    else setMaxPurchaseDate(today);

    if (values.purchaseDate) {
      let purchaseMoment = moment(values.purchaseDate, "YYYY-MM-DD");
      let valMoment = moment(val, "YYYY-MM-DD");
      if (purchaseMoment.isAfter(valMoment)) {
        //purchase date is after other date, reset
        setFieldValue("purchaseDate", "", false);
      }
    }
  };

  useEffect(() => {
    // Iterate over the Vial Meta
    // If the page names match
    // Set the options that will be used when a user picks the vial strength
    //
    // Note: This happens in the background. These are not options that the user should be selecting
    vialMeta.forEach((item) => {
      if (item.page === formPageName) {
        setPageVialMeta(item.types);
      }
    });
  });

  const setSelectedVialMeta = (event) => {
    // When a vial strength is selected
    // set the meta data.

    pageVialMeta.forEach((item) => {
      // get the vial strength selected
      const selectedVialStrength = event.target.value;

      // set the meta based on the selected vial strength
      if (selectedVialStrength === item.strength) {
        setItem(item.item);
        setInternalName(item.internalName);
        setItemDescription(item.itemDescription);
      }
    });
  };

  return (
    <div>
      {submissionMessage && showSubmissionMessage ? (
        <div className={styles.message}>
          {submissionMessage.split("\n").map((val, i) => (
            <div key={i}>{val}</div>
          ))}
        </div>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={Schema}
          validateOnBlur={false}
          validateOnChange={false}
          onSubmit={(values) => {
            setErrorMessage(null);
            setFileError(null);

            // Set the values of the vial meta
            values.item = item;
            values.internalName = internalName;
            values.itemDescription = itemDescription;

            let fileName = `images/${formName}/${new Date().getTime()}.${getExtension(
              values.upload
            )}`;

            const storageRef = firebase.storage.ref().child(fileName);
            return storageRef
              .put(values.upload)
              .then((snapshot) => {
                return snapshot.ref
                  .getDownloadURL()
                  .then(function (downloadURL) {
                    let valuesCopy = { ...values };
                    delete valuesCopy.upload;
                    valuesCopy.licenseURL = downloadURL;
                    if (dateField && dateField.name) {
                      valuesCopy[dateField.name] = updateDateFormat(
                        valuesCopy[dateField.name]
                      );
                    }
                    valuesCopy.purchaseDate = updateDateFormat(
                      valuesCopy.purchaseDate
                    );
                    valuesCopy.date = updateDateFormat(valuesCopy.date);

                    let newFormRef = firebase.db.ref(formName).push();
                    return newFormRef.set(valuesCopy, (error) => {
                      if (error) {
                        console.error(error);
                        setErrorMessage(
                          "Something went wrong. Please try again."
                        );
                      } else {
                        setShowSubmissionMessage(true);
                        window.scrollTo(0, 0);
                      }
                    });
                  });
              })
              .catch((error) => {
                console.error(error);
                setFileError(
                  "Sorry! Something went when uploading your file. Please try again"
                );
              });
          }}
        >
          {({ errors, touched, setFieldValue, isSubmitting, values }) => (
            <Form>
              <div className={styles.requiredNotes}>
                * all fields are required
              </div>

              <div className={styles.formSection}>
                {dateField ? (
                  <div
                    className={`${styles.half} ${
                      errors[dateField.name] && touched[dateField.name]
                        ? styles.inputError
                        : ""
                    }`}
                  >
                    <label className={styles.label}>{dateField.label}:</label>

                    <Field
                      name={dateField.name}
                      className={styles.input}
                      type="date"
                      onChange={(e) =>
                        updateDateField(
                          e.currentTarget.value,
                          values,
                          setFieldValue
                        )
                      }
                      max={today}
                    />
                  </div>
                ) : null}
                <div
                  className={`${styles.half} ${
                    errors.requestedVials && touched.requestedVials
                      ? styles.inputError
                      : ""
                  }`}
                >
                  <label className={styles.label}>
                    Replacement Vial Quantity Requested:
                  </label>

                  <Field
                    name="requestedVials"
                    className={styles.input}
                    type="number"
                    min="0"
                  />
                </div>
              </div>

              <div className={styles.formSection}>
                <div
                  className={`${styles.half} ${
                    errors.vialStrength && touched.vialStrength
                      ? styles.inputError
                      : ""
                  }`}
                >
                  <label className={styles.label}>
                    Replacement Vial Strength Requested:
                  </label>

                  <Field
                    as="select"
                    name="vialStrength"
                    className={styles.input}
                    onBlur={(event) => setSelectedVialMeta(event)}
                  >
                    <option>--Select an option--</option>
                    <option value="GIAPREZA 0.5 mg vial NDC: 68547-005-01">
                      GIAPREZA 0.5 mg vial NDC: 68547-005-01
                    </option>
                    <option value="GIAPREZA 2.5 mg vial NDC: 68547-501-02">
                      GIAPREZA 2.5 mg vial NDC: 68547-501-02
                    </option>
                  </Field>
                </div>

                <div
                  className={`${styles.half} ${
                    errors.purchaseDate && touched.purchaseDate
                      ? styles.inputError
                      : ""
                  }`}
                >
                  <label className={styles.label}>Purchase Date:</label>

                  <Field
                    name="purchaseDate"
                    className={styles.input}
                    type="date"
                    max={maxPurchaseDate}
                  />
                </div>
              </div>

              <div className={styles.formSection}>
                <div
                  className={`${styles.half} ${
                    errors.invoiceNumber && touched.invoiceNumber
                      ? styles.inputError
                      : ""
                  }`}
                >
                  <label className={styles.label}>Invoice Number:</label>

                  <Field name="invoiceNumber" className={styles.input} />
                </div>
              </div>

              <div className={styles.sectionLabel}>Contact Information:</div>
              <div className={styles.formSection}>
                <div
                  className={`${styles.full} ${
                    errors.contact &&
                    errors.contact.hospitalName &&
                    touched.contact &&
                    touched.contact.hospitalName
                      ? styles.inputError
                      : ""
                  }`}
                >
                  <label className={styles.label}>Hospital Name:</label>

                  <Field
                    name="contact.hospitalName"
                    type="text"
                    className={styles.input}
                  />
                </div>
              </div>

              <div className={styles.formSection}>
                <div
                  className={`${styles.half} ${
                    errors.contact &&
                    errors.contact.authorizedRepresentativeName &&
                    touched.contact &&
                    touched.contact.authorizedRepresentativeName
                      ? styles.inputError
                      : ""
                  }`}
                >
                  <label className={styles.label}>
                    Authorized Representative Name:
                  </label>

                  <Field
                    name="contact.authorizedRepresentativeName"
                    className={styles.input}
                  />
                </div>

                <div
                  className={`${styles.half} ${
                    errors.contact &&
                    errors.contact.authorizedRepresentativeTitle &&
                    touched.contact &&
                    touched.contact.authorizedRepresentativeTitle
                      ? styles.inputError
                      : ""
                  }`}
                >
                  <label className={styles.label}>
                    Authorized Representative Title:
                  </label>

                  <Field
                    name="contact.authorizedRepresentativeTitle"
                    className={styles.input}
                  />
                </div>
              </div>

              <div className={styles.formSection}>
                <div
                  className={`${styles.half} ${
                    errors.contact &&
                    errors.contact.email &&
                    touched.contact &&
                    touched.contact.email
                      ? styles.inputError
                      : ""
                  }`}
                >
                  <label className={styles.label}>Email Address:</label>

                  <Field
                    name="contact.email"
                    className={styles.input}
                    type="email"
                  />
                </div>

                <div
                  className={`${styles.half} ${
                    errors.contact &&
                    errors.contact.phoneNumber &&
                    touched.contact &&
                    touched.contact.phoneNumber
                      ? styles.inputError
                      : ""
                  }`}
                >
                  <label className={styles.label}>Phone Number:</label>

                  <Field
                    name="contact.phoneNumber"
                    className={styles.input}
                    type="tel"
                  />
                </div>
              </div>

              <div className={styles.sectionLabel}>Shipping Information:</div>

              <div className={styles.formSection}>
                <div
                  className={`${styles.full} ${
                    errors.shipping &&
                    errors.shipping.address1 &&
                    touched.shipping &&
                    touched.shipping.address1
                      ? styles.inputError
                      : ""
                  }`}
                >
                  <label className={styles.label}>Address 1:</label>

                  <Field
                    name="shipping.address1"
                    type="text"
                    className={styles.input}
                  />
                </div>
              </div>

              <div className={styles.formSection}>
                <div
                  className={`${styles.full} ${
                    errors.shipping &&
                    errors.shipping.address2 &&
                    touched.shipping &&
                    touched.shipping.address2
                      ? styles.inputError
                      : ""
                  }`}
                >
                  <label className={styles.label}>Address 2:</label>

                  <Field
                    name="shipping.address2"
                    type="text"
                    className={styles.input}
                  />
                </div>
              </div>

              <div className={styles.formSection}>
                <div
                  className={`${styles.third} ${
                    errors.shipping &&
                    errors.shipping.city &&
                    touched.shipping &&
                    touched.shipping.city
                      ? styles.inputError
                      : ""
                  }`}
                >
                  <label className={styles.label}>City:</label>

                  <Field name="shipping.city" className={styles.input} />
                </div>

                <div
                  className={`${styles.third} ${
                    errors.shipping &&
                    errors.shipping.state &&
                    touched.shipping &&
                    touched.shipping.state
                      ? styles.inputError
                      : ""
                  }`}
                >
                  <label className={styles.label}>State:</label>

                  <Field
                    name="shipping.state"
                    className={styles.input}
                    type="tel"
                  />
                </div>

                <div
                  className={`${styles.third} ${
                    errors.shipping &&
                    errors.shipping.zipCode &&
                    touched.shipping &&
                    touched.shipping.zipCode
                      ? styles.inputError
                      : ""
                  }`}
                >
                  <label className={styles.label}>Zip Code:</label>

                  <Field name="shipping.zipCode" className={styles.input} />
                </div>
              </div>

              <div className={styles.formSection}>
                <div
                  className={`${styles.full} ${
                    errors.purchaseOrderNumber && touched.purchaseOrderNumber
                      ? styles.inputError
                      : ""
                  }`}
                >
                  <label className={styles.label}>Purchase Order Number:</label>

                  <Field
                    name="purchaseOrderNumber"
                    type="text"
                    className={styles.input}
                  />
                </div>
              </div>

              <div className={styles.uploadSection}>
                <div className={styles.uploadLabel}>
                  Submit a copy of your hospital state license (PDF, PNG or JPEG
                  file):
                </div>

                <div className={styles.uploadContainer}>
                  <div className={styles.uploadButton}>Upload</div>

                  <input
                    name="upload"
                    className={styles.uploadInput}
                    accept="image/jpeg, image/jpg, image/png, application/pdf"
                    type="file"
                    onChange={(event) => {
                      setFieldValue("upload", event.currentTarget.files[0]);
                    }}
                  />

                  {values.upload ? (
                    <div className={styles.uploadName}>
                      {values.upload.name}
                    </div>
                  ) : null}

                  {fileError ? (
                    <div
                      className={styles.errorMessage}
                      style={{
                        marginLeft: "20px",
                        paddingLeft: "20px",
                        paddingRight: "20px",
                      }}
                    >
                      {fileError}
                    </div>
                  ) : errors.upload && touched.upload ? (
                    <div
                      className={styles.errorMessage}
                      style={{
                        marginLeft: "20px",
                        paddingLeft: "20px",
                        paddingRight: "20px",
                      }}
                    >
                      {errors.upload}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className={styles.checkboxesWrapper}>
                <label
                  className={`${styles.checkboxSection} ${
                    errors.authorizationVerification &&
                    touched.authorizationVerification
                      ? styles.checkboxError
                      : ""
                  }`}
                >
                  <Field
                    className={styles.checkInput}
                    name="authorizationVerification"
                    type="checkbox"
                    id="authorizationVerification"
                  />

                  <span
                    className={`${styles.checkmark} ${
                      values.authorizationVerification ? styles.checked : ""
                    }`}
                  ></span>

                  <span className={styles.checkboxLabel}>
                    I am authorized to submit this application and to make the
                    following representations and agreements on behalf of the
                    hospital listed above. I represent and warrant that the
                    information provided in this application is accurate and
                    complete to the best of my knowledge.
                  </span>
                </label>

                <label
                  className={`${styles.checkboxSection} ${
                    errors.termsVerify && touched.termsVerify
                      ? styles.checkboxError
                      : ""
                  }`}
                >
                  <Field
                    className={styles.checkInput}
                    name="termsVerify"
                    type="checkbox"
                    id="termsVerify"
                  />

                  <span
                    className={`${styles.checkmark} ${
                      values.termsVerify ? styles.checked : ""
                    }`}
                  ></span>

                  <span className={styles.checkboxLabel}>
                    I acknowledge that I have read and understood the GIAPREZA
                    (angiotensin II) {formNameReadable} Terms and Conditions,
                    and that this request complies with such terms and
                    conditions.
                  </span>
                </label>
              </div>

              <div className={styles.formSection}>
                <div
                  className={`${styles.half} ${
                    errors.eSignature && touched.eSignature
                      ? styles.inputError
                      : ""
                  }`}
                >
                  <label className={styles.label}>E-Signature:</label>

                  <Field name="eSignature" className={styles.input} />
                </div>

                <div
                  className={`${styles.half} ${
                    errors.date && touched.date ? styles.inputError : ""
                  }`}
                >
                  <label className={styles.label}>Date:</label>

                  <Field name="date" className={styles.input} type="date" />
                </div>
              </div>

              <div className={styles.submitContainer}>
                {errorMessage ? (
                  <div className={styles.errorMessage}>{errorMessage}</div>
                ) : showErrors(errors, touched) ? (
                  <div className={styles.errorMessage}>
                    Error: Please complete all fields
                  </div>
                ) : null}

                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <Spinner /> : null}
                  <span
                    className={isSubmitting ? styles.submitButtonSpacing : null}
                  >
                    Submit
                  </span>
                </button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
}
